<template>
  <div id="sections">
    <v-container fluid>
      <v-flex xs12>
        <HowToUseThisPage />
        <div class="sections-container">
          <div class="route-container mb-4 section-heading">
            <h1>
              <v-icon class="sections-icon">sections_icon</v-icon>
              {{ $t('sections.pageTitle').toUpperCase() }}
            </h1>
          </div>
          <v-flex
            v-if="
              isAuthenticated &&
              checkAuthorization(['superAdmin', 'admin']).length > 0
            "
            class="d-flex mb-7 mt-12"
          >
            <div class="">
              <v-btn
                v-if="!this.openSectionForm"
                color="rgba(165, 216, 103, 1)"
                class="elevation-0 font-weight-bold"
                @click="addNewHandler"
                >{{ $t('sections.createNewSection') }}</v-btn
              >
              <div v-else class="close-link" @click="closebuilder">
                <v-icon>arrowleft</v-icon>
                <span class="close-link-text">{{ $t('sections.close') }}</span>
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-text-field
              v-if="!openSectionForm"
              v-model="searchList"
              :append-inner-icon="searchList ? '' : 'mdi-magnify'"
              :label="$t('sections.search')"
              single-line
              hide-details
              clearable
              variant="solo"
              flat
              density="compact"
              color="#E8E9EC"
              style="max-width: 265px"
            ></v-text-field>
          </v-flex>
          <v-form v-if="openSectionForm" class="mb-12" ref="addeditform">
            <v-card class="py-10 px-15">
              <v-card-title class="px-0 font-weight-bold font-size-32">{{
                editingSection
                  ? $t('sections.editSection')
                  : $t('sections.sectionBuilder')
              }}</v-card-title>
              <v-card-text
                class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                >{{ $t('sections.sectionTitle') }}</v-card-text
              >
              <v-text-field
                v-model="sectionTitle"
                variant="outlined"
                :placeholder="$t('sections.sectionTitlePlaceholder')"
                :rules="[rules.required, rules.length(2)]"
                class="user-input mt-1"
                color="#A0C548"
                :loading="isediting"
                style="min-width: 100%"
              ></v-text-field>
              <v-card-text
                class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                >{{ $t('sections.category') }}</v-card-text
              >
              <v-combobox
                v-model="sectionCategory"
                :items="allCategories"
                item-title="name"
                :return-object="true"
                class=""
                :label="$t('sections.categoryPlaceholder')"
                multiple
                chips
                color="#A0C548"
                variant="outlined"
                :loading="isediting"
                @update:model-value="updateCategories"
                style="min-width: 100%"
              >
              </v-combobox>

              <v-divider class="mt-4 mb-5"></v-divider>

              <!-- ===== LANGUAGE SELECTION (NEW) ===== -->
              <v-card-text
                class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                >{{ $t('sections.requiredLanguage') }}</v-card-text
              >
              <v-alert
                type="info"
                density="compact"
                variant="outlined"
                class="mt-2 mb-4"
              >
                <div
                  class="text-caption"
                  v-html="
                    $t('sections.languageInfo', {
                      language: requiredLanguage
                        ? availableLanguages.find(
                            (l) => l.code === requiredLanguage
                          )?.label
                        : $t('sections.selectedLanguage'),
                    })
                  "
                ></div>
              </v-alert>

              <v-radio-group v-model="requiredLanguage" inline class="mt-2">
                <v-radio
                  v-for="lang in availableLanguages"
                  :key="lang.code"
                  :label="lang.label"
                  :value="lang.code"
                  color="primary"
                ></v-radio>
              </v-radio-group>

              <v-divider class="mt-4 mb-5"></v-divider>

              <v-card-text
                class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                >{{ $t('sections.uploadPdf') }}</v-card-text
              >

              <v-file-input
                v-model="file"
                accept=".pdf"
                color="#a5d867"
                counter
                :hint="$t('sections.oneFileHint')"
                persistent-hint
                :placeholder="$t('sections.selectYourFiles')"
                prepend-icon="mdi-paperclip"
                show-size
                :label="$t('sections.selectYourFiles')"
                variant="underlined"
                style="min-width: 100%"
              >
                <template #selection="{ fileNames }">
                  <template v-if="fileNames && fileNames.length">
                    <v-chip
                      v-for="(name, i) in fileNames.slice(0, 2)"
                      :key="i"
                      :style="{ backgroundColor: '#a5d867', color: 'white' }"
                      label
                      size="small"
                      class="mr-2"
                    >
                      {{ name }}
                    </v-chip>
                    <span
                      v-if="fileNames.length > 2"
                      class="text-overline text-grey-darken-3 mx-2"
                    >
                      +{{ fileNames.length - 2 }} File(s)
                    </span>
                  </template>
                </template>
              </v-file-input>

              <v-chip
                v-if="uploadedFile"
                class="ma-2"
                color="#a5d867"
                closable
                :style="{ backgroundColor: '#a5d867', color: 'white' }"
                @click:close="removeFile(uploadedFile, 'pdf')"
              >
                {{ uploadedFile }}
              </v-chip>

              <v-divider class="mt-4 mb-5"></v-divider>

              <!-- ===== UNITS SELECTION (UPDATED TO USE FILTERED LIST) ===== -->
              <v-card flat class="py-5" v-if="requiredLanguage">
                <v-layout wrap class="mb-5">
                  <div
                    class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                  >
                    {{ $t('sections.selectYourUnits') }}
                    <span v-if="filteredUnitsByLanguage.length > 0"
                      >({{
                        $t('sections.unitsAvailable', {
                          count: filteredUnitsByLanguageWithSearch.length,
                        })
                      }})</span
                    >
                  </div>
                  <v-spacer></v-spacer>
                  <div
                    class="search-container"
                    v-if="filteredUnitsByLanguage.length > 0"
                  >
                    <v-text-field
                      v-model="searchUnits"
                      color="#E8E9EC"
                      class="search-field"
                      variant="outlined"
                      flat
                      clearable
                      density="compact"
                      label="Search"
                      :append-inner-icon="searchUnits ? '' : 'mdi-magnify'"
                      single-line
                      hide-details
                      style="max-width: 265px"
                    ></v-text-field>
                  </div>
                </v-layout>

                <!-- No Units Available Message -->
                <v-alert
                  v-if="filteredUnitsByLanguage.length === 0"
                  type="warning"
                  variant="outlined"
                  class="mb-4"
                >
                  <div class="text-subtitle-2 font-weight-bold mb-2">
                    {{
                      $t('sections.noUnitsAvailable', {
                        language: availableLanguages.find(
                          (l) => l.code === requiredLanguage
                        )?.label,
                      })
                    }}
                  </div>
                  <div class="text-caption">
                    {{
                      $t('sections.translateUnitsFirst', {
                        language: availableLanguages.find(
                          (l) => l.code === requiredLanguage
                        )?.label,
                      })
                    }}
                  </div>
                  <v-btn
                    size="small"
                    color="primary"
                    variant="outlined"
                    class="mt-3"
                    :to="{ name: 'Units' }"
                  >
                    {{ $t('sections.goToUnitsBuilder') }}
                  </v-btn>
                </v-alert>

                <!-- Units Grid -->
                <div class="units-collection-scroll" v-else>
                  <v-row class="units-collection-row">
                    <v-col
                      v-for="unit in filteredUnitsByLanguageWithSearch"
                      :key="unit.id"
                      cols="12"
                      md="6"
                      class="pa-0"
                    >
                      <v-checkbox
                        class="mt-0 pa-0 black-circle-checkboxes units-checkboxes"
                        density="compact"
                        :model-value="selectedUnitIds.includes(unit.id)"
                        @update:model-value="(val) => toggleUnit(unit, val)"
                        :id="unit.id"
                        :disabled="unit.disabled || unit.tempdisabled"
                        true-icon="mdi-radiobox-marked"
                        false-icon="mdi-radiobox-blank"
                        style="min-width: 100%"
                      >
                        <!-- eye preview button on the left -->
                        <template #prepend>
                          <v-icon
                            class="mr-1 handle-none"
                            @click.stop="previewUnit(unit)"
                          >
                            eye
                          </v-icon>
                        </template>

                        <!-- rich label: title + (optional) description -->
                        <template #label>
                          <div class="d-flex flex-column">
                            <span
                              :class="[
                                'text-subtitle-2',
                                'text-uppercase',
                                selectedUnits.includes(unit)
                                  ? 'font-weight-bold'
                                  : 'font-weight-regular',
                              ]"
                            >
                              {{ getDisplayTitle(unit, requiredLanguage) }}
                            </span>
                          </div>
                        </template>
                      </v-checkbox>
                    </v-col>
                  </v-row>
                </div>
              </v-card>

              <!-- Message if no language selected yet -->
              <v-alert v-else type="info" variant="outlined" class="mt-4">
                <div class="text-subtitle-2 font-weight-bold">
                  {{ $t('sections.pleaseSelectLanguage') }}
                </div>
                <div class="text-caption">
                  {{ $t('sections.chooseLanguagePrompt') }}
                </div>
              </v-alert>

              <div v-if="selectedUnits.length" class="">
                <v-divider class="mb-6"></v-divider>
                <div
                  class="pa-0 pt-2 mb-6 text-subtitle-1 font-weight-bold text-uppercase"
                >
                  {{ $t('sections.orderYourUnits') }}
                  <span
                    v-if="requiredLanguage"
                    class="text-caption font-weight-regular ml-3"
                  >
                    {{ requiredLanguage.toUpperCase() }}:
                    {{ visibleSelectedUnitsCount }}/{{ selectedUnits.length }}
                  </span>
                </div>
              </div>
              <draggable
                v-model="selectedUnits"
                item-key="id"
                handle=".handle"
                group="people"
                @start="drag = true"
                @end="drag = false"
              >
                <template #item="{ element }">
                  <v-list
                    v-show="
                      !requiredLanguage ||
                      hasLocalizedContent(element, requiredLanguage)
                    "
                    rounded
                    border
                    class="border-grey mb-5 py-0"
                    :class="element.id"
                    :key="element.id"
                  >
                    <v-list-item class="selected-units">
                      <template #prepend>
                        <v-icon class="handle">drag_icon</v-icon>
                      </template>

                      <v-list-item-title
                        class="text-uppercase text-subtitle-2 font-weight-bold"
                      >
                        {{ getDisplayTitle(element, requiredLanguage) }}
                      </v-list-item-title>

                      <template #append>
                        <v-icon
                          :icon="element.locked ? 'lock' : 'lock_open'"
                          :class="[
                            'lock-toggle',
                            { 'is-locked': element.locked },
                          ]"
                          @click="lockUnit(element)"
                        />
                      </template>
                    </v-list-item>
                  </v-list>
                </template>
              </draggable>
              <v-divider class="mt-4 mb-5"></v-divider>
              <v-card
                v-if="
                  isAuthenticated &&
                  checkAuthorization(['superAdmin']).length > 0 &&
                  editingSection
                "
                class="d-flex align-center"
                flat
              >
                <div>
                  {{ $t('sections.churchAvailability') }}
                </div>
                <div class="ml-10">
                  <v-radio-group
                    v-model="type"
                    inline
                    :disabled="
                      !checkAuthorization(['superAdmin']).length > 0 &&
                      editingSection
                    "
                  >
                    <v-radio
                      :label="$t('sections.yes')"
                      value="public"
                    ></v-radio>
                    <v-radio
                      :label="$t('sections.no')"
                      value="private"
                    ></v-radio>
                  </v-radio-group>
                </div>
              </v-card>
              <v-card
                class="py-10 px-15"
                flat
                :color="isDarkTheme ? '#323232' : '#f8f8f8'"
              >
                <v-card-title class="px-0 font-weight-bold font-size-32">{{
                  $t('sections.editSectionDescription')
                }}</v-card-title>
                <v-card-text
                  class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                  >{{ $t('sections.featuredImage') }}</v-card-text
                >
                <v-file-input
                  v-model="featureImage"
                  accept=".jpg,.jpeg,.png"
                  color="#a5d867"
                  counter
                  :hint="$t('sections.oneImageHint')"
                  persistent-hint
                  :placeholder="$t('sections.selectYourImage')"
                  prepend-icon="mdi-paperclip"
                  show-size
                  :label="$t('sections.selectYourImage')"
                  variant="underlined"
                  style="min-width: 100%"
                >
                  <template #selection="{ fileNames }">
                    <v-chip
                      v-if="fileNames && fileNames.length"
                      variant="outlined"
                      color="#a5d867"
                      class="mr-2"
                      :style="{ backgroundColor: '#a5d867', color: 'white' }"
                      label
                      size="small"
                    >
                      {{ fileNames[0] }}
                    </v-chip>
                  </template>
                </v-file-input>

                <v-chip
                  v-if="uploadedFeatureImage"
                  class="ma-2"
                  color="#a5d867"
                  closable
                  :style="{ backgroundColor: '#a5d867', color: 'white' }"
                  @click:close="removeFile(uploadedFeatureImage, 'featured')"
                >
                  {{ uploadedFeatureImage }}
                </v-chip>

                <v-card-text
                  class="pa-0 pt-7 pb-4 text-subtitle-1 font-weight-bold text-uppercase"
                  >{{ $t('sections.overviewContent') }}</v-card-text
                >
                <v-card flat>
                  <CustomQuill
                    id="section-overview-editor-1"
                    ref="sectioneditor1"
                    :model-value="sectionOverviewEditor"
                    @update:model-value="onSectionOverviewEditorChange"
                    :placeholder="$t('sections.enterTextHere')"
                    :key="`section-overview-${$i18n.locale}`"
                  />
                </v-card>

                <v-card-text
                  class="pa-0 pt-7 pb-0 text-subtitle-1 font-weight-bold text-uppercase"
                  >{{ $t('sections.authorName') }}</v-card-text
                >
                <v-text-field
                  v-model="authorName"
                  variant="outlined"
                  flat
                  :placeholder="$t('sections.authorNamePlaceholder')"
                  class="user-input mt-5"
                  color="#A0C548"
                  style="min-width: 100%"
                ></v-text-field>

                <v-card-text
                  class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                  >{{ $t('sections.authorImage') }}</v-card-text
                >

                <v-file-input
                  v-model="authorImage"
                  accept=".jpg,.jpeg,.png"
                  color="#a5d867"
                  counter
                  :hint="$t('sections.oneImageHint')"
                  persistent-hint
                  :placeholder="$t('sections.selectYourImage')"
                  prepend-icon="mdi-paperclip"
                  show-size
                  :label="$t('sections.selectYourImage')"
                  variant="underlined"
                  style="min-width: 100%"
                >
                  <template #selection="{ fileNames }">
                    <v-chip
                      v-if="fileNames && fileNames.length"
                      variant="outlined"
                      color="#a5d867"
                      class="mr-2"
                      :style="{ backgroundColor: '#a5d867', color: 'white' }"
                      label
                      size="small"
                    >
                      {{ fileNames[0] }}
                    </v-chip>
                  </template>
                </v-file-input>

                <!-- Avatar preview -->
                <div v-if="authorPreview" class="mt-3">
                  <v-avatar size="64">
                    <v-img :src="authorPreview" alt="Author Image Preview" />
                  </v-avatar>
                </div>

                <v-chip
                  v-if="uploadedAuthorImage"
                  class="ma-2"
                  color="#a5d867"
                  closable
                  :style="{ backgroundColor: '#a5d867', color: 'white' }"
                  @click:close="removeFile(uploadedAuthorImage, 'author')"
                >
                  {{ uploadedAuthorImage }}
                </v-chip>

                <v-card-text
                  class="px-0 pt-7 pb-4 text-subtitle-1 font-weight-bold text-uppercase"
                  >{{ $t('sections.authorBioContent') }}</v-card-text
                >
                <v-card flat>
                  <!-- <QuillEditor
                    id="author-bio-editor-2"
                    ref="editor2"
                    class="editor-2 mb-6"
                    :modules="customModulesForEditor"
                    :toolbar="editorSettings.toolbar"
                    :theme="'snow'"
                    v-model="authorBio"
                    placeholder="Enter Description Here"
                  /> -->
                  <CustomQuill
                    id="author-bio-editor-2"
                    ref="editor2"
                    v-model="authorBio"
                    :placeholder="$t('sections.enterTextHere')"
                    :key="`author-bio-${$i18n.locale}`"
                  />
                </v-card>
              </v-card>
            </v-card>
            <div class="text-center">
              <v-btn
                color="#592892"
                class="elevation-0 font-weight-bold my-14 px-14"
                @click="editingSection ? editSection() : addSection()"
                >{{ authorizedActions }}</v-btn
              >

              <v-btn
                color="rgba(165, 216, 103, 1)"
                class="elevation-0 font-weight-bold my-14 ml-7 px-14"
                :loading="isPreviewingSection"
                :disabled="isPreviewingSection"
                @click="previewSection()"
                >{{ $t('sections.previewSection') }}
                <template #loader>
                  <span>Loading...</span>
                  <span class="custom-loader">
                    <v-icon>cached</v-icon>
                  </span>
                </template>
              </v-btn>
            </div>
            <v-progress-linear
              :indeterminate="iseditingSection"
              :active="iseditingSection"
              color="#592892"
            ></v-progress-linear>
            <v-alert
              v-if="feedback"
              border="start"
              color="#592892"
              class="text-center"
            >
              {{ feedback }}
            </v-alert>
            <v-divider class="dashed"></v-divider>
          </v-form>
          <v-data-table
            :headers="adminActions"
            fixed-header
            :loading="isloading"
            :loading-text="$t('sections.loadingSections')"
            :no-data-text="$t('sections.noSectionsFound')"
            :items="sections"
            :items-per-page="20"
            :header-props="{ sortIcon: '$sortarrowdown' }"
            :footer-props="{ 'items-per-page-options': [10, 20, 30, 40, 50] }"
            item-key="id"
            :search="searchList"
            class="elevation-1 users-table"
          >
            <template #progress>
              <v-progress-linear
                color="#592892"
                indeterminate
              ></v-progress-linear>
            </template>

            <template v-if="isAuthenticated" #[`item.type`]="{ item }">
              <v-chip
                v-if="
                  item.cid == churchid ||
                  checkAuthorization(['superAdmin', 'admin']).length > 0
                "
                size="small"
                class="px-5"
                :class="[
                  item.status == 'verified'
                    ? isDarkTheme
                      ? 'text-grey-lighten-3'
                      : 'text-black'
                    : isDarkTheme
                    ? 'text-black'
                    : 'text-grey-lighten-1',
                ]"
                :color="
                  item.status == 'verified' ? 'rgba(165, 216, 103, .32)' : ''
                "
                variant="flat"
              >
                {{ $helpers.uppercase(item.type) }}
              </v-chip>
            </template>

            <template v-if="isAuthenticated" #[`item.status`]="{ item }">
              <v-chip
                v-if="
                  item.cid == churchid ||
                  checkAuthorization(['superAdmin']).length > 0
                "
                size="small"
                class="px-5"
                :class="[
                  item.status == 'verified'
                    ? isDarkTheme
                      ? 'text-grey-lighten-3'
                      : 'text-black'
                    : isDarkTheme
                    ? 'text-black'
                    : 'text-grey-lighten-1',
                ]"
                :color="
                  item.status == 'verified' ? 'rgba(165, 216, 103, .32)' : ''
                "
                variant="flat"
              >
                {{ $helpers.uppercase(item.status) }}
              </v-chip>
            </template>

            <!-- NEW: Language pill -->
            <template #[`item.requiredLanguage`]="{ item }">
              <v-chip
                size="small"
                :color="getLanguageColor(item.requiredLanguage || 'en')"
                variant="outlined"
              >
                {{ (item.requiredLanguage || 'en').toUpperCase() }}
              </v-chip>
            </template>

            <template v-if="isAuthenticated" #[`item.featured`]="{ item }">
              <div v-if="checkAuthorization(['superAdmin']).length > 0">
                <v-tooltip
                  location="bottom"
                  :disabled="numFeaturedSections < 4"
                >
                  <template #activator="{ props }">
                    <span v-bind="props">
                      <v-checkbox
                        color="#A5D867"
                        class="ma-0 pt-0 featured-switch"
                        false-icon="mdi-star"
                        true-icon="mdi-star"
                        v-model="item.featured"
                        hide-details
                        :disabled="
                          !checkAuthorization(['superAdmin']).length > 0 ||
                          (numFeaturedSections >= 4 && !item.featured)
                        "
                        @update:model-value="setSectionToFeatured(item)"
                      >
                      </v-checkbox>
                    </span>
                  </template>
                  <div class="text-center">
                    <p class="my-0">You can only have 4 featured sections.</p>
                  </div>
                </v-tooltip>
              </div>
              <div v-else>
                <v-icon :color="item.active ? '#A5D867' : '#E0E0E0'"
                  >mdi-star</v-icon
                >
              </div>
            </template>

            <template #[`item.uis`]="{ item }">
              <div v-if="item.units.length">
                {{ item.units.length }}
              </div>
              <div v-else>
                <v-btn
                  v-if="!isHelpDesk()"
                  variant="flat"
                  size="small"
                  class="add-units-link"
                  @click="loadSectionToEdit(item)"
                  >Add UNITS</v-btn
                >
              </div>
            </template>
            <template v-if="isAuthenticated" #[`item.actions`]="{ item }">
              <div class="action-icons">
                <v-icon
                  v-if="
                    item.cid == churchid ||
                    checkAuthorization(['superAdmin', 'admin', 'leader'])
                      .length > 0
                  "
                  @click="previewSectionInfo(item)"
                  class="mr-3 admin-list-preview-section"
                >
                  mdi-information
                </v-icon>
                <v-icon
                  v-if="
                    item.cid == churchid ||
                    checkAuthorization(['superAdmin', 'admin', 'leader'])
                      .length > 0
                  "
                  @click="previewSection(item)"
                  class="mr-3"
                >
                  eye
                </v-icon>
                <v-icon
                  v-if="
                    !isHelpDesk() &&
                    (item.cid == churchid ||
                      checkAuthorization(['superAdmin']).length > 0)
                  "
                  @click="loadSectionToEdit(item)"
                  class="mr-3"
                  >edit</v-icon
                >
                <v-icon
                  v-if="
                    (item.cid == churchid ||
                      checkAuthorization(['superAdmin']).length > 0) &&
                    !isHelpDesk()
                  "
                  @click="deleteItem(item, 'sections')"
                  >delete_icon</v-icon
                >
              </div>
            </template>
          </v-data-table>
        </div>
      </v-flex>
      <!-- Dialogs -->
      <PreviewPopup
        v-model:showSectionInfo="showSectionInfo"
        :previewSectionInfoMeta="previewSectionInfoMeta"
        @listevent="handlerPopup"
      />
    </v-container>
  </div>
</template>

<script>
  import firebase from 'firebase'
  import db from '@/firebase/init'
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage'
  import PreviewPopup from '@/components/utilities/PreviewPopup'
  import { mapGetters } from 'vuex'
  import draggable from 'vuedraggable'
  import CustomQuill from '@/components/utilities/CustomQuill.vue'
  import { confirmAction } from '@/components/utilities/SwalService'
  import Swal from 'sweetalert2'
  import { currentTheme } from '@/plugins/vuetify'
  import { useLanguages } from '@/helpers/useLanguages'
  import { openPreview } from '@/helpers/previewBridge'
  import { transformUnitForLanguage } from '@/helpers/previewContent'
  import { matchesConfirmWord } from '@/helpers/confirmWord'

  export default {
    name: 'Sections',
    components: {
      draggable,
      HowToUseThisPage,
      PreviewPopup,
      CustomQuill,
    },
    setup() {
      const { enabledLangs, defaultLang } = useLanguages()
      return { enabledLangs, defaultLang }
    },
    data() {
      return {
        feedback: null,
        backdoor: 0,
        sections: [],
        allSections: [],
        sectionTitle: '',
        sectionCategory: [],
        allCategories: [],
        newCategory: [],
        requiredLanguage: null, // NEW: Required language for section
        units: [],
        expanded: [],
        file: null,
        featureImage: null,
        authorImage: null,
        type: '',
        uploadedFile: '',
        uploadedFeatureImage: '',
        uploadedAuthorImage: '',
        uploadedFileURL: '',
        uploadedFeatureImageURL: '',
        uploadedAuthorImageURL: '',
        fullPath: null,
        uploadTask: '',
        uploadStarted: false,
        removeFileDialog: false,
        progressUpload: 0,
        openSectionForm: false,
        singleExpand: true,
        isloading: true,
        isFormValid: false,
        isediting: false,
        activeindex: null,
        searchList: '', // top table search
        searchUnits: '',
        searchUsers: '',
        filtered: '',
        leader: '',
        selectedUnits: [],
        // Language-specific selections kept in-memory so switching languages doesn't merge
        selectedUnitsByLang: {},
        selectedUnitIds: [],
        tempdisabled: false,
        editMembersDialog: false,
        editingSection: false,
        iseditingSection: false,
        sectionToEdit: null,
        isPreviewingSection: false,
        sectionOverview: '',
        sectionOverviewEditor: '',
        authorBio: '',
        authorName: '',
        somethingHasChanged: false,
        showSectionInfo: false,
        previewSectionInfoMeta: null,
        editStatus: '',
        createdBy: '',
        prevType: '',
        prevStatus: '',
        authorObjectURLCache: null,
        rules: {
          length: (len) => (v) =>
            (v || '').length >= len || `min ${len} characters required `,
          required: (v) => !!v || 'This field is required',
        },
        uploadRules: {},
        filters: [
          'A',
          'B',
          'C',
          'D',
          'E',
          'F',
          'G',
          'H',
          'I',
          'J',
          'K',
          'L',
          'M',
          'N',
          'O',
          'P',
          'Q',
          'R',
          'S',
          'T',
          'U',
          'V',
          'W',
          'X',
          'Y',
          'Z',
        ],
      }
    },
    computed: {
      churchid() {
        return this.$store.getters['AppData/cid']
      },
      isDarkTheme() {
        return currentTheme.value === 'dark'
      },
      ...mapGetters('UserData', ['isAuthenticated']),
      ...mapGetters('AppData', ['cid']),
      adminActions() {
        let h = [
          {
            title: this.$t('sections.tableHeaders.section'),
            sortable: true,
            key: 'title',
            width: '20%',
          },
          {
            title: this.$t('sections.tableHeaders.cid'),
            sortable: true,
            key: 'cid',
          },
          {
            title: this.$t('sections.tableHeaders.type'),
            sortable: true,
            key: 'type',
          },
          {
            title: this.$t('sections.tableHeaders.status'),
            sortable: true,
            key: 'status',
          },
          {
            title: this.$t('sections.tableHeaders.lang'),
            sortable: true,
            key: 'requiredLanguage',
          }, // NEW: Language column (abbreviated)
          {
            title: this.$t('sections.tableHeaders.units'),
            sortable: false,
            key: 'uis',
          },
          {
            title: this.$t('sections.tableHeaders.featured'),
            sortable: true,
            filterable: false,
            key: 'featured',
          },
          {
            title: this.$t('sections.tableHeaders.actions'),
            sortable: false,
            key: 'actions',
            align: 'end',
            width: '250',
          },
        ]
        if (this.isAuthenticated) {
          if (this.checkAuthorization(['admin']).length > 0)
            h = [
              {
                title: this.$t('sections.tableHeaders.section'),
                sortable: true,
                key: 'title',
              },
              {
                title: this.$t('sections.tableHeaders.type'),
                sortable: true,
                key: 'type',
              },
              {
                title: this.$t('sections.tableHeaders.status'),
                sortable: true,
                key: 'status',
              },
              {
                title: this.$t('sections.tableHeaders.lang'),
                sortable: true,
                key: 'requiredLanguage',
              }, // NEW: Language column (abbreviated)
              {
                title: this.$t('sections.tableHeaders.units'),
                sortable: false,
                key: 'uis',
              },
              // { text: 'Featured', sortable: true, filterable: false, value: 'featured' },
              {
                title: this.$t('sections.tableHeaders.actions'),
                sortable: false,
                key: 'actions',
                align: 'end',
              },
            ]
          else if (this.checkAuthorization(['leader']).length > 0)
            h = [
              {
                title: this.$t('sections.tableHeaders.section'),
                sortable: true,
                key: 'title',
              },
              {
                title: this.$t('sections.tableHeaders.lang'),
                sortable: true,
                key: 'requiredLanguage',
              }, // NEW: Language column (abbreviated)
              {
                title: this.$t('sections.tableHeaders.actions'),
                sortable: false,
                key: 'actions',
                align: 'end',
              },
            ]
        }
        return h
      },
      sectionList() {
        return this.sections
      },
      unitsList() {
        // this backdoor is used to allow a method to recompute this property
        this.backdoor

        return this.units.filter((item) => {
          if (item.title !== undefined) {
            return item.title
          }
        })
      },
      filteredItems() {
        return this.unitsList.filter((item) => {
          if (this.searchUnits) {
            return item.title
              .toLowerCase()
              .includes(this.searchUnits.toLowerCase())
          } else {
            return item.title
              .toLowerCase()
              .includes(this.filtered.toLowerCase())
          }
        })
      },

      authorizedActions() {
        if (this.isAuthenticated) {
          return this.editingSection
            ? this.checkAuthorization(['superAdmin']).length > 0 &&
              this.editStatus !== 'verified'
              ? this.$t('sections.saveAndApprove')
              : this.$t('sections.publish')
            : this.$t('sections.addSection')
          // this.checkAuthorization(['superAdmin']).length > 0 ? 'ADD SECTION' : 'SUBMIT FOR APPROVAL'
        }
        return false
      },
      numFeaturedSections() {
        return this.allSections.filter((value) => value.featured).length
      },
      authorPreview() {
        // prefer live-selected file preview; otherwise show stored URL
        return this.authorObjectURLCache || this.uploadedAuthorImageURL || ''
      },
      // NEW: Language-aware computed properties
      availableLanguages() {
        return this.enabledLangs.map((lang) => ({
          code: lang.code,
          label: lang.label,
        }))
      },
      filteredUnitsByLanguage() {
        if (!this.requiredLanguage) {
          return []
        }
        return this.unitsList.filter((unit) =>
          this.hasLocalizedContent(unit, this.requiredLanguage)
        )
      },
      // Filtered units with search applied (searches both English and selected language titles)
      filteredUnitsByLanguageWithSearch() {
        if (!this.requiredLanguage) {
          return []
        }
        
        let units = this.filteredUnitsByLanguage
        
        // Apply search filter if searchUnits is provided
        if (this.searchUnits && this.searchUnits.trim()) {
          const searchTerm = this.searchUnits.toLowerCase().trim()
          units = units.filter((unit) => {
            // Search in the display title (language-specific)
            const displayTitle = this.getDisplayTitle(unit, this.requiredLanguage).toLowerCase()
            // Also search in English title as fallback
            const englishTitle = (unit.title || '').toLowerCase()
            // Search in the selected language title directly (for better matching)
            const selectedLangTitle = unit.localizedContent?.[this.requiredLanguage]?.title?.toLowerCase() || ''
            
            return (
              displayTitle.includes(searchTerm) ||
              englishTitle.includes(searchTerm) ||
              selectedLangTitle.includes(searchTerm)
            )
          })
        }
        
        return units
      },
      visibleSelectedUnitsCount() {
        if (!this.requiredLanguage) return this.selectedUnits.length
        return this.selectedUnits.filter((u) =>
          this.hasLocalizedContent(u, this.requiredLanguage)
        ).length
      },
    },
    watch: {
      churchid: {
        deep: true,
        handler(event) {
          this.getSections(event)
        },
      },
      // On language change, swap the visible ordered list with that language's slice
      requiredLanguage: {
        handler(newLang, oldLang) {
          if (!newLang) return
          if (oldLang) {
            // persist current list into previous language slot (by id only to reduce reactive churn)
            this.selectedUnitsByLang[oldLang] = (this.selectedUnits || []).map(
              (u) => ({ id: u.id, locked: !!u.locked })
            )
          }
          const next = this.selectedUnitsByLang[newLang] || []
          // Rehydrate from ids to full unit objects from unitsList if available
          const byId = new Map(this.unitsList.map((u) => [u.id, u]))
          this.selectedUnits = next
            .map((s) => {
              const found = byId.get(s.id)
              return found ? { ...found, locked: !!s.locked } : null
            })
            .filter(Boolean)
        },
      },
      // Sync selectedUnitsByLang when selectedUnits is modified (e.g., via drag-and-drop)
      selectedUnits: {
        deep: true,
        handler() {
          if (this.requiredLanguage && this.selectedUnits) {
            this.selectedUnitsByLang[this.requiredLanguage] =
              this.selectedUnits.map((u) => ({ id: u.id, locked: !!u.locked }))
          }
        },
      },
      authorImage: {
        handler(file) {
          const URL_API =
            typeof window !== 'undefined' && (window.URL || window.webkitURL)

          // Revoke previous object URL (if any)
          if (this.authorObjectURLCache && URL_API) {
            try {
              URL_API.revokeObjectURL(this.authorObjectURLCache)
            } catch (e) {
              /* noop */
            }
            this.authorObjectURLCache = null
          }

          // Create a new object URL when a file is selected
          if (file && file.name && URL_API) {
            try {
              this.authorObjectURLCache = URL_API.createObjectURL(file)
            } catch (e) {
              this.authorObjectURLCache = null
            }
          }
        },
        immediate: false,
      },
    },
    beforeUnmount() {
      const URL_API =
        typeof window !== 'undefined' && (window.URL || window.webkitURL)
      if (this.authorObjectURLCache && URL_API) {
        try {
          URL_API.revokeObjectURL(this.authorObjectURLCache)
        } catch (e) {
          /* noop */
        }
        this.authorObjectURLCache = null
      }
    },
    methods: {
      // NEW: Language helper methods
      hasLocalizedContent(unit, languageCode) {
        // Check if unit has localizedContent for the specified language
        if (unit.localizedContent && unit.localizedContent[languageCode]) {
          const lc = unit.localizedContent[languageCode]
          // Check if there's at least a title
          return !!(lc.title || lc.subtitle)
        }
        // Fallback: check top-level fields for English
        if (languageCode === 'en') {
          return !!(unit.title || unit.subtitle)
        }
        return false
      },
      getDisplayTitle(unit, languageCode) {
        // Returns the language-specific title for display purposes
        if (!unit) return ''
        
        // If language is specified and unit has localized content
        if (languageCode && unit.localizedContent && unit.localizedContent[languageCode]) {
          const localizedTitle = unit.localizedContent[languageCode].title
          // Return localized title if it exists and is not empty
          if (localizedTitle && localizedTitle.trim()) {
            return localizedTitle
          }
        }
        
        // Fallback to English/top-level title
        return unit.title || ''
      },
      getLanguageColor(languageCode) {
        const colors = {
          en: 'blue',
          pt: 'green',
          es: 'orange',
          fr: 'purple',
          de: 'red',
        }
        return colors[languageCode] || 'grey'
      },
      getAvailableLanguagesForUnit(unit) {
        const languages = []
        this.enabledLangs.forEach((lang) => {
          if (this.hasLocalizedContent(unit, lang.code)) {
            languages.push({
              code: lang.code,
              name: lang.label,
            })
          }
        })
        return languages
      },
      setSectionToFeatured(item) {
        db.collection('sections')
          .doc(item.id)
          .update({
            featured: item.featured,
          })
          .catch((err) => {
            console.log(err)
          })
      },
      addNewHandler() {
        // this backdoor is used to trigger the unitsList computed property
        this.backdoor++

        this.sectionTitle = ''
        this.selectedUnits = []
        this.selectedUnitsByLang = {}
        this.uploadedFile = ''
        this.uploadedFeatureImage = ''
        this.uploadedAuthorImage = ''
        this.sectionOverview = ''
        this.sectionOverviewEditor = ''
        this.selectedUnitIds = []
        this.searchUnits = ''
        this.authorBio = ''
        this.authorName = ''
        this.file = null
        this.featureImage = null
        this.authorImage = null
        this.uploadedFileURL = ''
        this.uploadedFeatureImageURL = ''
        this.uploadedAuthorImageURL = ''
        this.requiredLanguage = null // NEW: Reset language
        // this.type = ''
        if (this.editingSection) {
          if (this.$refs.addeditform) this.$refs.addeditform.reset()
          this.openSectionForm = true
        } else {
          this.openSectionForm = !this.openSectionForm
        }
        this.editingSection = false
        // console.log('this.openSectionForm ', this.openSectionForm)
      },
      async addSection() {
        if (!this.sectionTitle) {
          this.feedback = this.$t('sections.feedbackMissingTitle')
          return
        }

        try {
          let fileToUpload = null
          let featureImageToUpload = null
          let authorImageToUpload = null

          // PDF
          if (this.file && 'name' in this.file) {
            const meta = await this.uploadFile(this.file)
            fileToUpload = meta.name
            this.uploadedFileURL = meta.publicURL
          } else if (this.uploadedFile) {
            fileToUpload = this.uploadedFile
            this.uploadedFileURL = this.uploadedFileURL || null
          }

          // Featured image
          if (this.featureImage && 'name' in this.featureImage) {
            const meta = await this.uploadFile(this.featureImage)
            featureImageToUpload = meta.name
            this.uploadedFeatureImageURL = meta.publicURL
          } else if (this.uploadedFeatureImage) {
            featureImageToUpload = this.uploadedFeatureImage
            this.uploadedFeatureImageURL = this.uploadedFeatureImageURL || null
          }

          // Author image
          if (this.authorImage && 'name' in this.authorImage) {
            const meta = await this.uploadFile(this.authorImage)
            authorImageToUpload = meta.name
            this.uploadedAuthorImageURL = meta.publicURL
          } else if (this.uploadedAuthorImage) {
            authorImageToUpload = this.uploadedAuthorImage
            this.uploadedAuthorImageURL = this.uploadedAuthorImageURL || null
          }

          // Selected unit IDs
          // const listOfUnitIDs = this.selectedUnits.map((u) => ({ id: u.id }))
          const listOfUnitIDs = this.selectedUnits.map((u) => ({
            id: u.id,
            locked: !!u.locked,
          }))

          // Categories: keep existing, add new and wait for IDs
          const updatedSectionCategory = this.sectionCategory
            .filter((c) => c.id !== undefined)
            .map((c) => ({ id: c.id }))

          if (this.newCategory.length) {
            const created = await Promise.all(
              this.newCategory.map((name) =>
                db.collection('categories').add({ name })
              )
            )
            created.forEach((docRef) =>
              updatedSectionCategory.push({ id: docRef.id })
            )
          }

          const churchPin =
            this.checkAuthorization(['superAdmin']).length > 0 ? null : this.cid

          const getUser = this.$store.getters['UserData/getUser']

          const payload = {
            cid: churchPin,
            units: listOfUnitIDs,
            title: this.sectionTitle,
            category: updatedSectionCategory,
            status: 'verified',
            type:
              this.checkAuthorization(['superAdmin']).length > 0
                ? 'public'
                : 'private',
            active: true,

            uploadedFile: fileToUpload || null,
            uploadedFeatureImage: featureImageToUpload || null,
            uploadedAuthorImage: authorImageToUpload || null,

            uploadedFileURL: this.uploadedFileURL || null,
            uploadedFeatureImageURL: this.uploadedFeatureImageURL || null,
            uploadedAuthorImageURL: this.uploadedAuthorImageURL || null,

            sectionOverview: this.sectionOverview || '',
            authorBio: this.authorBio || '',
            authorName: this.authorName || '',
            createdBy: getUser.uid,
            requiredLanguage: this.requiredLanguage || 'en', // NEW: Save required language
          }

          // Strip only undefined (keep nulls)
          Object.keys(payload).forEach(
            (k) => payload[k] === undefined && delete payload[k]
          )

          await db.collection('sections').add(payload)

          // reset
          this.selectedUnits = []
          this.selectedUnitIds = []
          this.sectionTitle = ''
          this.sectionCategory = []
          this.requiredLanguage = null // NEW: Reset language
          this.openSectionForm = false
          this.uploadedFile = ''
          this.uploadedFeatureImage = ''
          this.uploadedAuthorImage = ''
          this.somethingHasChanged = false
          this.uploadedFileURL = ''
          this.uploadedFeatureImageURL = ''
          this.uploadedAuthorImageURL = ''
          this.file = null
          this.featureImage = null
          this.authorImage = null
          this.sectionOverviewEditor = ''
        } catch (err) {
          console.error(err)
          this.feedback = this.$t('sections.feedbackError')
        }
      },
      checkAuthorization(permissions) {
        const userRoles = this.$store.getters['UserData/getRoles']
        if (permissions && userRoles) {
          const normalizedPermissions = permissions.includes('superAdmin')
            ? [...permissions, 'helpDesk']
            : permissions
          const hasRole = normalizedPermissions.filter(() =>
            userRoles.some((role) => normalizedPermissions.includes(role))
          )
          return hasRole
        }
      },
      isHelpDesk() {
        const userRoles = this.$store.getters['UserData/getRoles']
        return Array.isArray(userRoles) && userRoles.includes('helpDesk')
      },
      closebuilder() {
        // console.log('closebuilder ', this.editingSection)
        // this backdoor is used to trigger the unitsList computed property
        this.backdoor++

        this.sectionTitle = ''
        this.sectionOverview = ''
        this.sectionOverviewEditor = ''
        this.selectedUnitIds = []
        this.searchUnits = ''
        if (this.editingSection) {
          if (this.$refs.addeditform) this.$refs.addeditform.reset()
        }

        this.openSectionForm = false
        this.editingSection = false
      },
      async deleteItem(item, collection) {
        if (this.isHelpDesk && this.isHelpDesk()) {
          const key = 'sections.messages.deleteNotAllowed'
          this.feedback =
            (this.$te && this.$te(key) ? this.$t(key) : null) ||
            'Help Desk users cannot delete sections.'
          return
        }

        if (!item || !collection) {
          Swal.fire({
            title: 'Houston we have a problem.',
            text: 'Error with the delete function, missing params.',
            icon: 'error',
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
          return
        }

        const title = collection === 'sections' ? item.title : 'this item'
        const images = [
          item.uploadedFile,
          item.uploadedFeatureImage,
          item.uploadedAuthorImage,
        ].filter(Boolean)

        const confirmed = await confirmAction({
          title: `You are about to delete: <br>"${title}"`,
          html: 'Type DELETE below to confirm.',
          input: 'text',
          inputPlaceholder: 'Type DELETE',
          inputValidator: (value) => {
            return new Promise((resolve) => {
              if (matchesConfirmWord(value, 'DELETE')) {
                resolve()
              } else {
                resolve('You need to type DELETE to confirm')
              }
            })
          },
          confirmButtonText: 'Yes Delete it!',
          cancelButtonText: 'No, Keep it!',
          context: this,
          buttonsStyling: false,
          customClass: {
            confirmButton:
              'swal-confirm-btn text-white text-uppercase text-body-2',
            cancelButton:
              'swal-cancel-btn text-white text-uppercase text-body-2',
          },
        })

        if (!confirmed) return

        try {
          if (images.length > 0) {
            const storage = firebase.storage().ref()
            for (const imagePath of images) {
              await this.deleteSectionStorageFile(storage, imagePath)
            }
          }

          await db.collection(collection).doc(item.id).delete()
        } catch (err) {
          console.error('Error deleting section:', err)
          Swal.fire({
            title: 'Houston we have a problem.',
            text: 'The section could not be deleted. Please try again.',
            icon: 'error',
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
        }
      },
      async editSection() {
        this.iseditingSection = true
        // section ? section : ''

        if (this.sectionTitle && this.sectionToEdit) {
          // console.log('1 ', this.file)
          let fileToUpload = null
          let featureImageToUpload = null
          let authorImageToUpload = null
          let fileToUploadURL = null
          let featureImageToUploadURL = null
          let authorImageToUploadURL = null

          // console.log('1 ')
          if (this.file && 'name' in this.file) {
            let fileToUploadMeta = await this.uploadFile(this.file)
            fileToUploadURL = fileToUploadMeta.publicURL
            fileToUpload = fileToUploadMeta.name
            // console.log('2 ', fileToUpload)
          } else if (this.uploadedFile) {
            fileToUpload = this.uploadedFile
            fileToUploadURL = this.uploadedFileURL
          }

          if (this.featureImage && 'name' in this.featureImage) {
            let featureImageToUploadMeta = await this.uploadFile(
              this.featureImage
            )
            featureImageToUploadURL = featureImageToUploadMeta.publicURL
            featureImageToUpload = featureImageToUploadMeta.name
          } else if (this.uploadedFeatureImage) {
            featureImageToUpload = this.uploadedFeatureImage
            featureImageToUploadURL = this.uploadedFeatureImageURL
          }

          if (this.authorImage && 'name' in this.authorImage) {
            let authorImageToUploadMeta = await this.uploadFile(
              this.authorImage
            )
            authorImageToUploadURL = authorImageToUploadMeta.publicURL
            authorImageToUpload = authorImageToUploadMeta.name
          } else if (this.uploadedAuthorImage) {
            authorImageToUpload = this.uploadedAuthorImage
            authorImageToUploadURL = this.uploadedAuthorImageURL
          }

          // console.log('continue ', fileToUpload)

          // filter out any new categories without IDs
          const updatedSectionCategory = this.sectionCategory.filter(
            (item) => item.id !== undefined
          )

          // if there was a new category add it to the collection
          if (this.newCategory.length) {
            for (let index = 0; index < this.newCategory.length; index++) {
              const cat = this.newCategory[index]
              db.collection('categories')
                .add({
                  name: cat,
                })
                .then((docRef) => {
                  // add new categories to the section category array
                  updatedSectionCategory.push({
                    id: docRef.id,
                  })
                })
            }
          }
          // remove the name property from the section category array before adding it to the collection
          updatedSectionCategory.map(function (item) {
            delete item.name
            return item
          })

          // setTimeout(() => {
          // get the id of each selected unit
          let listOfUnits = Object.keys(this.selectedUnits).map((key) => {
            return this.selectedUnits[key]
          })

          // Build union of all language selections (de-duplicated by id)
          // Use currently visible list as the base order, then add units from other languages
          const unionIdsMap = new Map()
          // First, add all units from currently visible list (preserves order)
          listOfUnits.forEach((u) => {
            if (u && u.id) {
              unionIdsMap.set(u.id, { id: u.id, locked: !!u.locked })
            }
          })
          // Then, add any units from other languages that aren't already included
          const arrToProcess = this.selectedUnitsByLang || {}
          for (const [lang, arr] of Object.entries(arrToProcess)) {
            // Skip current language since we already added it above
            if (lang === this.requiredLanguage) continue
            if (arr && Array.isArray(arr) && arr.length) {
              for (const u of arr) {
                if (u && u.id && !unionIdsMap.has(u.id)) {
                  unionIdsMap.set(u.id, { id: u.id, locked: !!u.locked })
                }
              }
            }
          }
          // Convert to array preserving order from currently visible list, then appending others
          const listOfUnitIDs = Array.from(unionIdsMap.values())

          let status =
            this.checkAuthorization(['superAdmin']).length > 0 ||
            this.type == 'private'
              ? 'verified'
              : 'unverified'

          db.collection('sections')
            .doc(this.sectionToEdit.id)
            .update({
              units: listOfUnitIDs,
              title: this.sectionTitle,
              category: updatedSectionCategory,
              status: status,
              type: this.type,
              uploadedFile: fileToUpload,
              uploadedFeatureImage: featureImageToUpload,
              uploadedAuthorImage: authorImageToUpload,
              uploadedFileURL: fileToUploadURL,
              uploadedFeatureImageURL: featureImageToUploadURL,
              uploadedAuthorImageURL: authorImageToUploadURL,
              sectionOverview: this.sectionOverview,
              authorBio: this.authorBio,
              authorName: this.authorName,
              requiredLanguage: this.requiredLanguage || 'en', // NEW: Save required language
            })
            .then(() => {
              // console.log('1 ', this.checkAuthorization(['superAdmin']).length > 0, this.prevStatus, status, this.prevType == 'private', this.type == 'public')

              if (
                !this.checkAuthorization(['superAdmin']).length > 0 &&
                this.type == 'public' &&
                this.prevStatus == 'verified'
              ) {
                // user edited a public section
                if (!this.checkAuthorization(['superAdmin']).length > 0) {
                  // console.log('user edited a public section - appdata/notification')
                  // Send Notification to Admin
                  this.$store.dispatch('AppData/notification', {
                    leader: this.$store.getters['UserData/getUser'],
                    meta: {
                      sectionTitle: this.sectionTitle,
                      cid: this.$store.getters['AppData/cid'],
                    },
                    type: 'Section edited',
                    cid: this.$store.getters['AppData/cid'],
                  })
                }
              }

              // the superAdmin approved the public, edited section by a church admin
              if (
                this.checkAuthorization(['superAdmin']).length > 0 &&
                this.type == 'public' &&
                this.prevStatus == 'unverified'
              ) {
                if (this.createdBy) {
                  // console.log('superAdmin approved the public, edited section - appdata/notification')
                  // Send Notification to Admin
                  this.$store.dispatch('AppData/notification', {
                    leader: this.createdBy,
                    meta: {
                      sectionTitle: this.sectionTitle,
                      cid: this.$store.getters['AppData/cid'],
                    },
                    type: 'Section approved',
                    cid: this.$store.getters['AppData/cid'],
                  })
                }
              }

              // Reset fields
              this.selectedUnits = []
              this.selectedUnitsByLang = {}
              this.selectedUnitIds = []
              this.sectionTitle = ''
              this.sectionCategory = ''
              this.uploadedFile = ''
              this.uploadedFeatureImage = ''
              this.uploadedAuthorImage = ''
              this.sectionOverview = ''
              this.sectionOverviewEditor = ''
              this.uploadedFileURL = ''
              this.uploadedFeatureImageURL = ''
              this.uploadedAuthorImageURL = ''
              this.type = ''
              this.editStatus = ''

              this.openSectionForm = false
              this.iseditingSection = false
              this.editingSection = false
              this.sectionToEdit = null

              this.somethingHasChanged = false

              // re-init the section list
              // this.getSections()
            })
          // }, 1000)
        } else {
          this.feedback = this.$t('sections.feedbackError') + ' '

          if (!this.sectionTitle)
            this.feedback += this.$t('sections.feedbackMissingTitle') + ' '
          if (!this.sectionToEdit)
            this.feedback += this.$t('sections.feedbackNoSectionToEdit') + ' '
        }
      },
      filterlist(letter, i) {
        this.activeindex = i
        this.filtered = letter.toLowerCase()
      },
      getLeader(id) {
        const leader = this.users.filter((x) => x.uid === id)
        return leader
      },
      loadSectionToEdit(section) {
        const s = JSON.parse(JSON.stringify(section))

        this.editingSection = true
        this.sectionToEdit = s
        this.editStatus = s.status
        this.selectedUnits = []
        this.selectedUnitsByLang = {}
        this.selectedUnitIds = [] // reset IDs array
        this.sectionCategory = []
        this.uploadedFile = ''
        this.uploadedFeatureImage = ''
        this.uploadedAuthorImage = ''
        this.sectionOverview = s.sectionOverview || ''
        this.sectionOverviewEditor = this.getOverviewEditorValue(
          this.sectionOverview
        )
        this.authorBio = s.authorBio || ''
        this.authorName = s.authorName || ''
        this.requiredLanguage = s.requiredLanguage || 'en' // NEW: Load language
        this.file = null
        // Initialize language slice from existing section units for the loaded language
        if (Array.isArray(s.units) && s.units.length) {
          const ids = s.units.map((u) => (u && u.id) || u)
          const byId = new Map(this.unitsList.map((u) => [u.id, u]))
          const initial = ids
            .map((id) => {
              const unit = byId.get(id)
              if (!unit) return null
              const unitLocked = s.units.find((u) => (u && u.id) === id)?.locked
              return {
                ...unit,
                checked: true,
                locked: !!unitLocked,
              }
            })
            .filter(Boolean)
          this.selectedUnits = initial
          this.selectedUnitIds = ids // populate selectedUnitIds from loaded units
          this.selectedUnitsByLang[this.requiredLanguage] = initial.map(
            (u) => ({ id: u.id, locked: !!u.locked })
          )
        }
        this.featureImage = null
        this.authorImage = null
        this.type = s.type
        this.createdBy = s.createdBy
        this.prevType = s.type
        this.prevStatus = s.status

        if (s.uploadedFile) {
          this.uploadedFile = s.uploadedFile
          this.uploadedFileURL = s.uploadedFileURL
        }
        if (s.uploadedFeatureImage) {
          this.uploadedFeatureImage = s.uploadedFeatureImage
          this.uploadedFeatureImageURL = s.uploadedFeatureImageURL
        }
        if (s.uploadedAuthorImage) {
          this.uploadedAuthorImage = s.uploadedAuthorImage
          this.uploadedAuthorImageURL = s.uploadedAuthorImageURL
        }

        // set categories
        if (s.category?.length) {
          this.sectionCategory = this.allCategories.filter((obj) =>
            s.category.some((obj2) => obj.id == obj2.id)
          )
        } else {
          this.sectionCategory = []
        }

        this.openSectionForm = true
        this.sectionTitle = s.title
        this.scrollToTop()
      },
      lockUnit(unit) {
        unit.locked = !unit.locked
      },
      normalizeEmptyHtml(html) {
        if (!html || html === '') return ''
        const textOnly = String(html).replace(/<[^>]*>/g, '').trim()
        return textOnly === '' ? '' : html
      },
      sanitizeQuillArtifacts(raw) {
        if (!raw || typeof raw !== 'string') return raw || ''
        if (typeof document === 'undefined') return raw.trim()

        const container = document.createElement('div')
        container.innerHTML = raw

        // Keep Quill list markup intact, but correct mismatched list intent:
        // li within <ol> should be ordered; li within <ul> should be bullet.
        container.querySelectorAll('ol > li[data-list]').forEach((li) => {
          li.setAttribute('data-list', 'ordered')
        })
        container.querySelectorAll('ul > li[data-list]').forEach((li) => {
          li.setAttribute('data-list', 'bullet')
        })

        return container.innerHTML.trim()
      },
      getOverviewEditorValue(raw) {
        return this.sanitizeQuillArtifacts(raw)
      },
      onSectionOverviewEditorChange(value) {
        const cleaned = this.sanitizeQuillArtifacts(value)
        this.sectionOverviewEditor = cleaned
        this.sectionOverview = this.normalizeEmptyHtml(cleaned)
      },
      previewSection(item) {
        this.feedback = ''

        if (item) {
          openPreview(this.$router, this.$route, {
            name: 'PreviewSection',
            id: item.id,
            lang: item.requiredLanguage || 'en',
          })
          return
        }

        if (this.sectionTitle && this.selectedUnits.length > 0) {
          const languageCode = this.requiredLanguage || 'en'
          const opened = openPreview(this.$router, this.$route, {
            name: 'PreviewSection',
            lang: languageCode,
            payload: {
              title: this.sectionTitle,
              selectedUnits: this.selectedUnits.map((unit) =>
                transformUnitForLanguage(unit, languageCode)
              ),
            },
          })
          if (!opened) {
            this.feedback =
              'Preview could not be opened. Try a saved module, or free some browser storage.'
          }
          return
        }

        if (!this.sectionTitle && this.selectedUnits.length === 0)
          this.feedback =
            'You need to have a Section title and Selected Units'
        if (!this.sectionTitle && this.selectedUnits.length > 0)
          this.feedback = 'You need to have a Section title'
        if (this.sectionTitle && this.selectedUnits.length === 0)
          this.feedback = 'You need to have Selected Units'
      },
      previewSectionInfo(item) {
        const sectionsUnits = item.units.map((unitID) =>
          this.units.find((el) => el.id == unitID.id)
        )
        const cloned = {
          ...JSON.parse(JSON.stringify(item)),
          units: sectionsUnits,
        }
        this.previewSectionInfoMeta = cloned
        this.showSectionInfo = true
      },
      previewUnit(unit) {
        openPreview(this.$router, this.$route, {
          name: 'PreviewUnit',
          id: unit.id,
          lang: this.requiredLanguage || 'en',
        })
      },
      handlerPopup() {
        this.showSectionInfo = false
      },
      async uploadFile(item) {
        const dot = item.name.lastIndexOf('.')
        const base = dot === -1 ? item.name : item.name.slice(0, dot)
        const ext = dot === -1 ? '' : item.name.slice(dot)
        const newname = `${base}_${Date.now()}${ext}`
        const storage = firebase.storage()
        const snapshot = await storage.ref('sections/' + newname).put(item)
        const meta = snapshot.metadata
        meta.publicURL = await snapshot.ref.getDownloadURL()
        return meta
      },
      async deleteSectionStorageFile(storage, imagePath) {
        const fullPath = `sections/${imagePath}`
        try {
          await storage.child(fullPath).delete()
        } catch (error) {
          if (error?.code !== 'storage/object-not-found') {
            console.warn(`Could not delete storage file ${fullPath}:`, error)
          }
        }
      },
      async removeFile(item, which) {
        const storage = firebase.storage().ref()

        if (!item) {
          Swal.fire({
            title: 'Houston we have a problem.',
            text: 'Error with the delete function, missing params.',
            icon: 'error',
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
          return
        }

        const confirmed = await confirmAction({
          title: `You are about to remove: <br><span class="text--red">"${item}"</span>`,
          html: 'Type REMOVE below to confirm.',
          input: 'text',
          inputPlaceholder: 'Type REMOVE',
          inputValidator: (value) => {
            return new Promise((resolve) => {
              if (matchesConfirmWord(value, 'REMOVE')) {
                resolve()
              } else {
                resolve('You need to type REMOVE to confirm')
              }
            })
          },
          confirmButtonText: 'Yes Remove it!',
          cancelButtonText: 'No, Keep it!',
          context: this,
        })

        if (!confirmed) return

        try {
          const fullPath = `sections/${item}`
          await storage.child(fullPath).delete()

          if (which === 'pdf') {
            this.uploadedFile = ''
            this.uploadedFileURL = ''
            this.file = null
          }

          if (which === 'featured') {
            this.uploadedFeatureImage = ''
            this.uploadedFeatureImageURL = ''
            this.featureImage = null
          }

          if (which === 'author') {
            this.uploadedAuthorImage = ''
            this.uploadedAuthorImageURL = ''
            this.authorImage = null

            // revoke preview URL too
            const URL_API =
              typeof window !== 'undefined' && (window.URL || window.webkitURL)
            if (this.authorObjectURLCache && URL_API) {
              try {
                URL_API.revokeObjectURL(this.authorObjectURLCache)
              } catch (e) {
                /* noop */
              }
              this.authorObjectURLCache = null
            }
          }

          await db.collection('sections').doc(this.sectionToEdit.id).update({
            uploadedFile: this.uploadedFile,
            uploadedFeatureImage: this.uploadedFeatureImage,
            uploadedAuthorImage: this.uploadedAuthorImage,
            uploadedFileURL: this.uploadedFileURL,
            uploadedFeatureImageURL: this.uploadedFeatureImageURL,
            uploadedAuthorImageURL: this.uploadedAuthorImageURL,
          })

          this.somethingHasChanged = true
        } catch (error) {
          console.error(`file delete error occurred: ${error}`)
        }
      },
      scrollToTop() {
        document
          .getElementById('sections')
          .scrollIntoView({ behavior: 'smooth' })
      },
      // unitSelected(u) {
      //   return this.unitsList.filter((item) => {
      //     if (item.id === u.id) {
      //       item.checked = !item.checked
      //       item.locked = false
      //     }
      //   })
      // },
      toggleUnit(unit, isChecked) {
        const idx = this.selectedUnitIds.indexOf(unit.id)

        if (isChecked && idx === -1) {
          // add
          this.selectedUnitIds.push(unit.id)
          // avoid dupes if already there somehow
          if (!this.selectedUnits.find((u) => u.id === unit.id)) {
            this.selectedUnits.push({ ...unit, locked: !!unit.locked })
          }
          // Sync to selectedUnitsByLang for current language
          if (this.requiredLanguage) {
            const langUnits =
              this.selectedUnitsByLang[this.requiredLanguage] || []
            if (!langUnits.find((u) => u.id === unit.id)) {
              langUnits.push({ id: unit.id, locked: !!unit.locked })
              this.selectedUnitsByLang[this.requiredLanguage] = langUnits
            }
          }
        } else if (!isChecked && idx > -1) {
          // remove
          this.selectedUnitIds.splice(idx, 1)
          this.selectedUnits = this.selectedUnits.filter(
            (u) => u.id !== unit.id
          )
          // Sync to selectedUnitsByLang for current language
          if (this.requiredLanguage) {
            const langUnits =
              this.selectedUnitsByLang[this.requiredLanguage] || []
            this.selectedUnitsByLang[this.requiredLanguage] = langUnits.filter(
              (u) => u.id !== unit.id
            )
          }
        }
      },
      updateCategories() {
        const allCat = this.allCategories
        const secCat = this.sectionCategory

        // Find values that are in secCat but not in allCat
        this.newCategory = secCat.filter(function (obj) {
          return !allCat.some(function (obj2) {
            return obj.name == obj2.name
          })
        })
      },
      init(cid) {
        this.isloading = true
        this.openSectionForm = false
        this.editingSection = false

        // we set a timeout for the User experience
        setTimeout(() => {
          this.units = []
          let unitsRef = db.collection('units')
          unitsRef
            .get()
            .then((snapshot) => {
              snapshot.forEach((doc) => {
                let unit = doc.data()
                unit.id = doc.id
                if (
                  unit.verified == 'verified' &&
                  (!unit.cid || (unit.cid && unit.cid == cid))
                ) {
                  this.units.push(unit)
                }
              })
            })
            .then(() => {
              this.allCategories = []
              let catRef = db.collection('categories')
              catRef.get().then((snapshot) => {
                snapshot.forEach((doc) => {
                  let cat = doc.data()
                  cat.id = doc.id
                  this.allCategories.push(cat)
                })
              })
            })
            .then(() => {
              this.isloading = false
            })
        }, 1000)
      },
      getSections(cid) {
        // console.log('cid ',cid)
        // const getUser = this.$store.getters['UserData/getUser']

        let sectionRef = db.collection('sections')
        sectionRef.onSnapshot((snapshot) => {
          this.sections = []
          this.allSections = []
          const isSuper =
            this.isAuthenticated &&
            this.checkAuthorization(['superAdmin']).length > 0
          snapshot.forEach((snap) => {
            let section = snap.data()
            section.id = snap.id

            const canView = isSuper
              ? true
              : (section.type === 'public' &&
                  section.status === 'verified') ||
                (section.cid && cid && section.cid == cid)

            if (canView) {
              this.allSections.push(section)
              this.sections.push(section)
            }

            if (
              this.isAuthenticated &&
              !this.checkAuthorization(['superAdmin']).length > 0
            ) {
              this.sections.sort((a) => {
                if (a.cid) return -1
                return 0
              })
            }
          })
          if (!this.somethingHasChanged) {
            this.init(cid)
          }
        })
      },
    },
    created() {
      this.getSections(this.$store.getters['AppData/cid'])
    },
  }
</script>

<style>
  .sections-container {
    margin-bottom: 25%;
  }
  .sections-container .section-heading h1 {
    display: flex;
    align-items: center;
  }
  .sections-container .sections-icon {
    margin-right: 10px;
  }
  .letter-filter-container {
    display: flex;
    align-items: center;
  }
  .letter-filter-container .clear-filter {
    margin: 0 5px;
  }
  .letter-filter-container ul {
    list-style: none;
    display: flex;
    padding-left: 0px;
  }
  .letter-filter-container ul li {
    margin-right: 5px;
    cursor: pointer;
  }
  .letter-filter-container ul li span:hover {
    font-weight: 900;
  }
  .letter-filter-container ul li span.active {
    font-weight: 900;
  }
  .search-field .v-text-field__details {
    display: none;
  }
  .v-input--is-label-active label {
    font-weight: 900;
  }
  .sections-container
    .v-input:not(.v-input--is-dirty).v-select.v-select--chips:not(
      .v-text-field--single-line
    ).v-text-field--enclosed
    .v-select__selections {
    min-height: inherit;
  }
  .v-autocomplete__content .v-list.v-select-list {
    display: flex;
    flex-wrap: wrap;
  }
  .v-autocomplete__content {
    max-width: 705px;
  }
  .v-autocomplete__content .v-list.v-select-list .v-list-item {
    flex: 1 1 50%;
  }
  .v-list-item--disabled img {
    filter: grayscale(1);
    opacity: 0.15;
  }
  .v-list-item__avatar {
    justify-content: center;
  }
  .sections-container .add-units-link {
    cursor: pointer;
  }
  .border-grey {
    border-color: #b5b5b5 !important;
  }
  .selected-units .v-list-item__icon {
    align-self: center;
  }
  .selected-units .v-list-item__icon,
  .admin-list-preview-section {
    cursor: pointer;
  }
  .selected-units .v-list-item__prepend .handle {
    cursor: grab;
  }
  .selected-units .lock-toggle,
  .v-icon.handle-none {
    opacity: 1;
  }

  .units-collection-scroll {
    max-height: 300px;
    overflow: auto;
    padding-top: 10px;
  }
  .units-collection-scroll .v-checkbox .v-selection-control {
    min-height: 0px; /* or whatever height you want */
  }

  /* kill the negative gutters just for this row */
  .units-collection-row {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  .units-collection-container .v-col {
    padding-top: 2px !important;
    padding-bottom: 2px !important;
  }

  .units-checkboxes {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  .v-input.featured-switch .theme--light.v-icon {
    color: rgb(164 164 164 / 54%);
  }
  .custom-unit-description-tooltip.menuable__content__active {
    opacity: 1;
    background: #a5d867;
    color: rgba(0, 0, 0, 0.75);
  }
  .units-checkboxes.v-input--dense > .v-input__control > .v-input__slot {
    margin-bottom: 0;
  }
  .units-checkboxes .v-messages {
    margin-bottom: 7px;
  }
  .units-checkboxes .v-messages .v-messages__message {
    font-size: 13px;
    padding-left: 32px;
    line-height: 1.3;
  }
</style>
